import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import type {
  DetailedCircuitGeometry,
  TrackContributionGpsCoord,
} from '@kangaroo/shared';

import type { ReferenceTrackGuideColors } from '../../lib/osmTrackOverlay';
import {
  trackCatalogSegmentColor,
  trackCatalogSegmentVisible,
  type TrackCatalogMapLayers,
} from '../../lib/trackCatalog';
import { loadMapKit } from '../../lib/mapkitLoader';
import {
  speedColorBrakeToFlatOut,
  trackSpeedRange,
  type TrackMapMarker,
  type TrackMapPoint,
} from '../../lib/telemetrySession';

type AppleMapsTrackMapProps = {
  points: TrackMapPoint[];
  trackGroups?: TrackMapPoint[][];
  referenceTrackGroups?: TrackMapPoint[][];
  speedRange?: { min: number; max: number };
  height?: number;
  title?: string;
  fill?: boolean;
  resetViewKey?: string | number;
  onMapClick?: () => void;
  onMapLatLngClick?: (coord: { lat: number; lon: number }) => void;
  onMapLatLngHover?: (coord: { lat: number; lon: number } | null) => void;
  highlightLines?: Array<{
    id: string;
    points: Array<{ lat: number; lon: number }>;
    color?: string;
    width?: number;
    opacity?: number;
    dash?: number[];
  }>;
  freehandDrawActive?: boolean;
  onMapLatLngDrawStroke?: (coord: { lat: number; lon: number }) => void;
  onMapMarkerClick?: (markerId: string) => void;
  onMapMarkerDragEnd?: (markerId: string, coord: { lat: number; lon: number }) => void;
  startFinishLine?: { pointA: { lat: number; lon: number }; pointB: { lat: number; lon: number } };
  crossTrackLines?: Array<{
    id: string;
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
    color?: string;
  }>;
  mapMarkers?: TrackMapMarker[];
  mapMarkerScale?: number;
  heatLineWidth?: number;
  heatLineHaloWidth?: number;
  fitBoundsPadding?: number;
  maxFitZoom?: number;
  onTilesReady?: () => void;
  mapViewport?: { lat: number; lon: number; zoom?: number };
  autoFitBounds?: boolean;
  referenceTrackStyle?: 'default' | 'guide';
  referenceTrackGuideColors?: ReferenceTrackGuideColors[];
  detailedCircuitPreview?: DetailedCircuitGeometry | null;
  corridorOutlineRings?: TrackContributionGpsCoord[][];
  showLapLines?: boolean;
};

type MapOverlay = mapkit.PolylineOverlay | mapkit.PolygonOverlay;

const FOLLOW_ZOOM = 19;
const DEFAULT_REFERENCE_GUIDE_COLORS: ReferenceTrackGuideColors = {
  line: '#94A3B8',
  arrow: '#E2E8F0',
  arrowOutline: '#07111C',
};

const DEFAULT_CIRCUIT_PREVIEW_LAYERS: TrackCatalogMapLayers = {
  trackOutline: true,
  corners: true,
  straights: true,
  sectors: true,
  lines: true,
  labels: false,
};

function mkCoord(lat: number, lon: number) {
  return new mapkit.Coordinate(lat, lon);
}

function coordsFromPoints(points: TrackMapPoint[]) {
  return points.map((point) => mkCoord(point.lat, point.lon));
}

function coordsFromContributionRing(ring: TrackContributionGpsCoord[]) {
  return ring.map((point) => mkCoord(point.lat, point.lon));
}

function contributionPaths(paths: TrackContributionGpsCoord[][]) {
  return paths.map((ring) => coordsFromContributionRing(ring));
}

function polylineStyle(options: {
  strokeColor: string;
  strokeOpacity?: number;
  lineWidth?: number;
  lineDash?: number[];
}) {
  return new mapkit.Style({
    strokeColor: options.strokeColor,
    strokeOpacity: options.strokeOpacity ?? 1,
    lineWidth: options.lineWidth ?? 3,
    lineDash: options.lineDash,
  });
}

function polygonStyle(options: {
  fillColor: string;
  fillOpacity?: number;
  strokeColor: string;
  strokeOpacity?: number;
  lineWidth?: number;
}) {
  return new mapkit.Style({
    fillColor: options.fillColor,
    fillOpacity: options.fillOpacity ?? 0.3,
    strokeColor: options.strokeColor,
    strokeOpacity: options.strokeOpacity ?? 1,
    lineWidth: options.lineWidth ?? 2,
  });
}

function addPolyline(
  map: mapkit.Map,
  overlays: MapOverlay[],
  coordinates: mapkit.Coordinate[],
  style: mapkit.Style,
) {
  if (coordinates.length < 2) {
    return;
  }

  const overlay = new mapkit.PolylineOverlay(coordinates, { style });
  map.addOverlay(overlay);
  overlays.push(overlay);
}

function addPolygon(
  map: mapkit.Map,
  overlays: MapOverlay[],
  coordinates: mapkit.Coordinate[],
  style: mapkit.Style,
) {
  if (coordinates.length < 3) {
    return;
  }

  const overlay = new mapkit.PolygonOverlay(coordinates, { style });
  map.addOverlay(overlay);
  overlays.push(overlay);
}

function zoomToSpan(zoom: number) {
  const delta = 360 / 2 ** zoom;
  return new mapkit.CoordinateSpan(delta, delta);
}

function drawCorridorOutlineRings(
  map: mapkit.Map,
  overlays: MapOverlay[],
  rings: TrackContributionGpsCoord[][],
) {
  for (const ring of rings) {
    if (ring.length < 3) {
      continue;
    }

    addPolygon(
      map,
      overlays,
      coordsFromContributionRing(ring),
      polygonStyle({
        fillColor: '#ffffff',
        fillOpacity: 0.05,
        strokeColor: '#ffffff',
        strokeOpacity: 0.85,
        lineWidth: 2,
      }),
    );
  }
}

function drawDetailedCircuitPreview(
  map: mapkit.Map,
  overlays: MapOverlay[],
  geometry: DetailedCircuitGeometry,
  layers: TrackCatalogMapLayers = DEFAULT_CIRCUIT_PREVIEW_LAYERS,
) {
  if (layers.trackOutline && geometry.outlineRings.length > 0) {
    for (const ring of geometry.outlineRings) {
      if (ring.length < 3) {
        continue;
      }
      addPolygon(
        map,
        overlays,
        coordsFromContributionRing(ring),
        polygonStyle({
          fillColor: '#ffffff',
          fillOpacity: 0.04,
          strokeColor: '#ffffff',
          strokeOpacity: 0.85,
          lineWidth: 2,
        }),
      );
    }
  }

  const orderedFeatures = [...geometry.features].sort((left, right) => {
    const rank = (type: string) => (type.toLowerCase() === 'sector' ? 0 : 1);
    return rank(left.type) - rank(right.type);
  });

  for (const feature of orderedFeatures) {
    if (!trackCatalogSegmentVisible(feature.type, layers)) {
      continue;
    }

    const paths = feature.polygonRings ?? [];
    if (paths.length === 0) {
      continue;
    }

    const color = trackCatalogSegmentColor(feature.type);
    const isSector = feature.type.toLowerCase() === 'sector';
    for (const ring of contributionPaths(paths)) {
      addPolygon(
        map,
        overlays,
        ring,
        polygonStyle({
          fillColor: color,
          fillOpacity: isSector ? 0.11 : 0.34,
          strokeColor: color,
          strokeOpacity: isSector ? 0.6 : 0.95,
          lineWidth: isSector ? 1.5 : 2,
        }),
      );
    }
  }

  if (geometry.centerLine && geometry.centerLine.length > 1) {
    addPolyline(
      map,
      overlays,
      coordsFromContributionRing(geometry.centerLine),
      polylineStyle({
        strokeColor: '#28c8ff',
        strokeOpacity: 0.9,
        lineWidth: 3,
      }),
    );
  }
}

function drawReferenceTrackOutlines(
  map: mapkit.Map,
  overlays: MapOverlay[],
  referenceTrackGroups: TrackMapPoint[][],
  style: 'default' | 'guide' = 'default',
  guideColors: ReferenceTrackGuideColors[] = [],
) {
  for (let groupIndex = 0; groupIndex < referenceTrackGroups.length; groupIndex += 1) {
    const group = referenceTrackGroups[groupIndex]!;
    const trackPath = coordsFromPoints(group);
    if (trackPath.length < 2) {
      continue;
    }

    if (style === 'guide') {
      const colors = guideColors[groupIndex] ?? DEFAULT_REFERENCE_GUIDE_COLORS;
      addPolyline(
        map,
        overlays,
        trackPath,
        polylineStyle({
          strokeColor: colors.line,
          strokeOpacity: 0.95,
          lineWidth: 3,
          lineDash: [2, 10],
        }),
      );
      continue;
    }

    addPolyline(
      map,
      overlays,
      trackPath,
      polylineStyle({
        strokeColor: '#07111C',
        strokeOpacity: 0.88,
        lineWidth: 7,
      }),
    );
    addPolyline(
      map,
      overlays,
      trackPath,
      polylineStyle({
        strokeColor: '#CBD5E1',
        strokeOpacity: 0.72,
        lineWidth: 3.5,
      }),
    );
  }
}

function drawCrossLine(
  map: mapkit.Map,
  overlays: MapOverlay[],
  pointA: { lat: number; lon: number },
  pointB: { lat: number; lon: number },
  color: string,
) {
  const line = [mkCoord(pointA.lat, pointA.lon), mkCoord(pointB.lat, pointB.lon)];
  addPolyline(map, overlays, line, polylineStyle({ strokeColor: '#FFFFFF', strokeOpacity: 0.92, lineWidth: 9 }));
  addPolyline(map, overlays, line, polylineStyle({ strokeColor: color, strokeOpacity: 1, lineWidth: 5 }));
}

function clearOverlays(map: mapkit.Map, overlays: MapOverlay[]) {
  if (overlays.length > 0) {
    map.removeOverlays(overlays);
    overlays.length = 0;
  }
}

function clearAnnotations(map: mapkit.Map, annotations: mapkit.MarkerAnnotation[]) {
  if (annotations.length > 0) {
    map.removeAnnotations(annotations);
    annotations.length = 0;
  }
}

export function AppleMapsTrackMap({
  points,
  trackGroups,
  referenceTrackGroups = [],
  speedRange,
  height = 292,
  title,
  fill = false,
  resetViewKey,
  onMapClick,
  onMapLatLngClick,
  onMapLatLngHover,
  highlightLines = [],
  freehandDrawActive = false,
  onMapLatLngDrawStroke,
  onMapMarkerClick,
  onMapMarkerDragEnd,
  startFinishLine,
  crossTrackLines = [],
  mapMarkers = [],
  mapMarkerScale = 1,
  heatLineWidth = 4.5,
  heatLineHaloWidth = 9,
  fitBoundsPadding = 68,
  onTilesReady,
  mapViewport,
  autoFitBounds = true,
  referenceTrackStyle = 'default',
  referenceTrackGuideColors = [],
  detailedCircuitPreview = null,
  corridorOutlineRings = [],
  showLapLines = true,
}: AppleMapsTrackMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapkit.Map | null>(null);
  const overlaysRef = useRef<MapOverlay[]>([]);
  const annotationsRef = useRef<mapkit.MarkerAnnotation[]>([]);
  const onTilesReadyRef = useRef(onTilesReady);
  const onMapLatLngClickRef = useRef(onMapLatLngClick);
  const onMapLatLngHoverRef = useRef(onMapLatLngHover);
  const onMapLatLngDrawStrokeRef = useRef(onMapLatLngDrawStroke);
  const onMapMarkerClickRef = useRef(onMapMarkerClick);
  const onMapMarkerDragEndRef = useRef(onMapMarkerDragEnd);
  const freehandDrawActiveRef = useRef(freehandDrawActive);
  const mapViewportRef = useRef(mapViewport);
  const resetViewKeyRef = useRef(resetViewKey);
  const lastFitViewKeyRef = useRef<string | number | undefined>(undefined);
  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const resolvedTrackGroups = useMemo(
    () => (trackGroups?.length ? trackGroups : points.length >= 2 ? [points] : []),
    [points, trackGroups],
  );
  const resolvedReferenceTrackGroups = useMemo(
    () => referenceTrackGroups.filter((group) => group.length >= 2),
    [referenceTrackGroups],
  );
  const resolvedReferenceTrackGuideColors = useMemo(
    () => referenceTrackGuideColors.slice(0, resolvedReferenceTrackGroups.length),
    [referenceTrackGuideColors, resolvedReferenceTrackGroups.length],
  );

  onTilesReadyRef.current = onTilesReady;
  onMapLatLngClickRef.current = onMapLatLngClick;
  onMapLatLngHoverRef.current = onMapLatLngHover;
  onMapLatLngDrawStrokeRef.current = onMapLatLngDrawStroke;
  onMapMarkerClickRef.current = onMapMarkerClick;
  onMapMarkerDragEndRef.current = onMapMarkerDragEnd;
  freehandDrawActiveRef.current = freehandDrawActive;
  mapViewportRef.current = mapViewport;
  resetViewKeyRef.current = resetViewKey;

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let cancelled = false;

    void loadMapKit()
      .then(() => {
        if (cancelled || !containerRef.current) {
          return;
        }

        const start =
          points[0] ??
          (mapViewportRef.current
            ? { lat: mapViewportRef.current.lat, lon: mapViewportRef.current.lon }
            : null);
        if (!start) {
          return;
        }

        const center = mkCoord(start.lat, start.lon);
        const zoom = mapViewportRef.current?.zoom ?? FOLLOW_ZOOM;
        const map = new mapkit.Map(containerRef.current, {
          mapType: mapkit.Map.MapTypes.Satellite,
          showsZoomControl: true,
          showsCompass: mapkit.FeatureVisibility.Hidden,
          isScrollEnabled: true,
          isZoomEnabled: true,
          isRotationEnabled: false,
        });
        map.region = new mapkit.CoordinateRegion(center, zoomToSpan(zoom));
        mapRef.current = map;
        setMapReady(true);
        onTilesReadyRef.current?.();
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Apple Maps failed to load.');
        }
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        clearOverlays(mapRef.current, overlaysRef.current);
        clearAnnotations(mapRef.current, annotationsRef.current);
        mapRef.current.destroy();
      }
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !mapViewport) {
      return;
    }

    map.setRegionAnimated(
      new mapkit.CoordinateRegion(
        mkCoord(mapViewport.lat, mapViewport.lon),
        zoomToSpan(mapViewport.zoom ?? FOLLOW_ZOOM),
      ),
      true,
    );
  }, [mapReady, mapViewport?.lat, mapViewport?.lon, mapViewport?.zoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    const handleSingleTap = (event: Event) => {
      if (freehandDrawActiveRef.current) {
        return;
      }

      const mapEvent = event as Event & { pointOnPage?: DOMPoint };
      if (!mapEvent.pointOnPage) {
        onMapClick?.();
        return;
      }

      const coordinate = map.convertPointOnPageToCoordinate(mapEvent.pointOnPage);
      onMapLatLngClickRef.current?.({
        lat: coordinate.latitude,
        lon: coordinate.longitude,
      });
    };

    map.addEventListener('single-tap', handleSingleTap);
    return () => {
      map.removeEventListener('single-tap', handleSingleTap);
    };
  }, [mapReady, onMapClick]);

  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!mapReady || !map || !container || !freehandDrawActive) {
      return;
    }

    let drawing = false;

    const coordFromEvent = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const point = new DOMPoint(event.clientX - rect.left, event.clientY - rect.top);
      const coordinate = map.convertPointOnPageToCoordinate(point);
      return { lat: coordinate.latitude, lon: coordinate.longitude };
    };

    const stopDrawing = () => {
      drawing = false;
      map.isScrollEnabled = true;
    };

    const onMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      drawing = true;
      map.isScrollEnabled = false;
      onMapLatLngDrawStrokeRef.current?.(coordFromEvent(event));
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!drawing) {
        return;
      }
      onMapLatLngDrawStrokeRef.current?.(coordFromEvent(event));
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopDrawing);

    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDrawing);
      map.isScrollEnabled = true;
    };
  }, [freehandDrawActive, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!mapReady || !map || !container || !onMapLatLngHoverRef.current) {
      return;
    }

    const onMouseMove = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const point = new DOMPoint(event.clientX - rect.left, event.clientY - rect.top);
      const coordinate = map.convertPointOnPageToCoordinate(point);
      onMapLatLngHoverRef.current?.({
        lat: coordinate.latitude,
        lon: coordinate.longitude,
      });
    };

    const onMouseLeave = () => {
      onMapLatLngHoverRef.current?.(null);
    };

    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseleave', onMouseLeave);
    return () => {
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    clearOverlays(map, overlaysRef.current);
    clearAnnotations(map, annotationsRef.current);

    if (detailedCircuitPreview) {
      drawDetailedCircuitPreview(map, overlaysRef.current, detailedCircuitPreview);
    } else if (corridorOutlineRings.length > 0) {
      drawCorridorOutlineRings(map, overlaysRef.current, corridorOutlineRings);
    }

    drawReferenceTrackOutlines(
      map,
      overlaysRef.current,
      resolvedReferenceTrackGroups,
      referenceTrackStyle,
      resolvedReferenceTrackGuideColors,
    );

    if (showLapLines) {
      for (const group of resolvedTrackGroups) {
        const trackPath = coordsFromPoints(group);
        const groupRange = speedRange ?? trackSpeedRange(group) ?? undefined;
        const speeds = group.map((point) => point.speedKph);
        const minSpeed = groupRange?.min ?? Math.min(...speeds);
        const maxSpeed = groupRange?.max ?? Math.max(...speeds);

        addPolyline(
          map,
          overlaysRef.current,
          trackPath,
          polylineStyle({
            strokeColor: '#07111C',
            strokeOpacity: 0.92,
            lineWidth: heatLineHaloWidth,
          }),
        );
        addPolyline(
          map,
          overlaysRef.current,
          trackPath,
          polylineStyle({
            strokeColor: '#FFFFFF',
            strokeOpacity: 0.95,
            lineWidth: heatLineWidth + 1.5,
          }),
        );

        for (let index = 1; index < group.length; index += 1) {
          const previous = group[index - 1]!;
          const current = group[index]!;
          const speed = (previous.speedKph + current.speedKph) / 2;
          addPolyline(
            map,
            overlaysRef.current,
            [mkCoord(previous.lat, previous.lon), mkCoord(current.lat, current.lon)],
            polylineStyle({
              strokeColor: speedColorBrakeToFlatOut(speed, minSpeed, maxSpeed),
              strokeOpacity: 0.82,
              lineWidth: heatLineWidth,
            }),
          );
        }
      }
    }

    for (const line of highlightLines) {
      addPolyline(
        map,
        overlaysRef.current,
        line.points.map((point) => mkCoord(point.lat, point.lon)),
        polylineStyle({
          strokeColor: line.color ?? '#F97316',
          strokeOpacity: line.opacity ?? 0.72,
          lineWidth: line.width ?? 11,
          lineDash: line.dash,
        }),
      );
    }

    if (startFinishLine) {
      drawCrossLine(
        map,
        overlaysRef.current,
        startFinishLine.pointA,
        startFinishLine.pointB,
        '#22C55E',
      );
    }

    for (const line of crossTrackLines) {
      drawCrossLine(map, overlaysRef.current, line.pointA, line.pointB, line.color ?? '#28c8ff');
    }

    for (const marker of mapMarkers) {
      const annotation = new mapkit.MarkerAnnotation(mkCoord(marker.lat, marker.lon), {
        title:
          marker.kind !== 'sector-boundary'
            ? marker.label
            : marker.selected
              ? marker.label
              : undefined,
        color: marker.color,
        glyphText: marker.kind === 'sector-boundary' ? undefined : marker.label.slice(0, 2),
        draggable:
          (marker.kind === 'note' || marker.kind === 'section-boundary') && Boolean(onMapMarkerDragEndRef.current),
      });
      annotation.displayPriority = marker.selected ? 1000 : 500;

      if (onMapMarkerClickRef.current && (marker.kind === 'note' || marker.kind === 'footage-start')) {
        annotation.addEventListener('select', () => {
          onMapMarkerClickRef.current?.(marker.id);
        });
      }

      if (
        onMapMarkerDragEndRef.current &&
        (marker.kind === 'note' || marker.kind === 'section-boundary')
      ) {
        annotation.addEventListener('drag-end', () => {
          onMapMarkerDragEndRef.current?.(marker.id, {
            lat: annotation.coordinate.latitude,
            lon: annotation.coordinate.longitude,
          });
        });
      }

      map.addAnnotations([annotation]);
      annotationsRef.current.push(annotation);
      void mapMarkerScale;
    }

    const fitCoords = [
      ...points.map((point) => mkCoord(point.lat, point.lon)),
      ...mapMarkers.map((marker) => mkCoord(marker.lat, marker.lon)),
    ];
    if (
      autoFitBounds &&
      fitCoords.length >= 2 &&
      resetViewKey !== undefined &&
      lastFitViewKeyRef.current !== resetViewKey
    ) {
      const region = mapkit.CoordinateRegion.fromCoordinates(fitCoords);
      const paddedSpan = new mapkit.CoordinateSpan(
        region.span.latitudeDelta * (1 + fitBoundsPadding / 400),
        region.span.longitudeDelta * (1 + fitBoundsPadding / 400),
      );
      map.setRegionAnimated(new mapkit.CoordinateRegion(region.center, paddedSpan), true);
      lastFitViewKeyRef.current = resetViewKey;
    }
  }, [
    autoFitBounds,
    corridorOutlineRings,
    crossTrackLines,
    detailedCircuitPreview,
    fitBoundsPadding,
    heatLineHaloWidth,
    heatLineWidth,
    highlightLines,
    mapMarkerScale,
    mapMarkers,
    mapReady,
    points,
    referenceTrackStyle,
    resetViewKey,
    resolvedReferenceTrackGuideColors,
    resolvedReferenceTrackGroups,
    resolvedTrackGroups,
    showLapLines,
    speedRange,
    startFinishLine,
  ]);

  const titleHeight = title ? 34 : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: fill ? '100%' : height,
        minHeight: fill ? 260 : height,
        bgcolor: '#020A12',
      }}
    >
      {title ? (
        <Typography
          sx={{
            px: 1.25,
            py: 0.75,
            fontSize: '0.78rem',
            fontWeight: 800,
            color: '#E2E8F0',
            borderBottom: '1px solid rgba(36,62,82,0.55)',
            height: titleHeight,
          }}
        >
          {title}
        </Typography>
      ) : null}
      {loadError ? (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', px: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#F8FAFC', fontWeight: 800, mb: 0.75 }}>
            Apple Maps could not load
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', color: '#94A3B8', lineHeight: 1.55 }}>{loadError}</Typography>
        </Box>
      ) : null}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: title ? `calc(100% - ${titleHeight}px)` : '100%',
          cursor: freehandDrawActive ? 'crosshair' : undefined,
        }}
      />
    </Box>
  );
}
